import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Tab bar for assembly guides — mobile only.
 * Scrolls to the target heading on click and keeps the active tab
 * in sync via IntersectionObserver while the user scrolls.
 *
 * @extends {Component<{}>}
 */
export class AssemblyGuidesTabsComponent extends Component {
    /** @type {IntersectionObserver | null} */
    #observer = null;

    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('click', this.#handleClick, {
            signal: this.#abortController.signal,
        });

        const firstTab = this.#tabs[0];
        if (firstTab?.dataset.anchor) {
            this.#setActiveTab(firstTab.dataset.anchor);
        }

        this.#initObserver();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#observer?.disconnect();
        this.#abortController.abort();
    }

    /** @returns {HTMLButtonElement[]} */
    get #tabs() {
        return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.assembly-guides__tab')]);
    }

    #handleClick = (/** @type {MouseEvent} */ event) => {
        const button = /** @type {Element} */ (event.target).closest('.assembly-guides__tab');
        if (!button) return;
        const anchor = /** @type {HTMLElement} */ (button).dataset.anchor;
        if (!anchor) return;
        const target = document.getElementById(`anchor-${anchor}`);
        if (target) {
            target.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
        }
        this.#setActiveTab(anchor);
    };

    /** @param {string} anchor */
    #setActiveTab(anchor) {
        this.#tabs.forEach((tab) => {
            const isActive = tab.dataset.anchor === anchor;
            tab.classList.toggle('assembly-guides__tab--active', isActive);
            tab.setAttribute('aria-pressed', String(isActive));
        });
    }

    #initObserver() {
        const anchors = this.#tabs
            .map((tab) => document.getElementById(`anchor-${tab.dataset.anchor}`))
            .filter(/** @param {HTMLElement | null} el */ (el) => el !== null);

        if (!anchors.length) return;

        this.#observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.#setActiveTab(entry.target.id.replace('anchor-', ''));
                        break;
                    }
                }
            },
            { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
        );

        anchors.forEach((el) => this.#observer?.observe(/** @type {HTMLElement} */ (el)));
    }
}

if (!customElements.get('assembly-guides-tabs-component')) {
    customElements.define('assembly-guides-tabs-component', AssemblyGuidesTabsComponent);
}
